import { useEffect, useLayoutEffect, useRef, useState } from "react";

import { useNotification } from "providers/NotificationProvider";

import FunctionalButton from "components/layout/FunctionalButton";
import NotificationPanel from "components/layout/notification/NotificationPanel";
import Portal from "components/layout/Portal";

import { ReactComponent as NotificationIcon } from "assets/svgs/Notification.svg";

const NotificationButton = ({ className = "", iconClassName = "w-4" }) => {
    const {
        showNotificationPanel,
        setShowNotificationPanel,
        hasUnreadNotifications,
        markAllNotificationsRead,
    } = useNotification();
    const buttonRef = useRef(null);
    const panelRef = useRef(null);
    const [panelPos, setPanelPos] = useState({ top: 0, right: 0 });

    useLayoutEffect(() => {
        if (!showNotificationPanel || !buttonRef.current) return undefined;

        const updatePos = () => {
            const rect = buttonRef.current.getBoundingClientRect();
            setPanelPos({
                top: rect.bottom + 8,
                right: Math.max(8, window.innerWidth - rect.right),
            });
        };

        updatePos();
        window.addEventListener("resize", updatePos);
        window.addEventListener("scroll", updatePos, true);
        return () => {
            window.removeEventListener("resize", updatePos);
            window.removeEventListener("scroll", updatePos, true);
        };
    }, [showNotificationPanel]);

    useEffect(() => {
        if (!showNotificationPanel) return undefined;

        markAllNotificationsRead();

        let handlePointer;
        let handleKey;
        // Attach after the opening click finishes so it doesn't immediately close.
        const timer = window.setTimeout(() => {
            handlePointer = (e) => {
                const target = e.target;
                if (buttonRef.current?.contains(target) || panelRef.current?.contains(target)) {
                    return;
                }
                setShowNotificationPanel(false);
            };
            handleKey = (e) => {
                if (e.key === "Escape") setShowNotificationPanel(false);
            };
            document.addEventListener("mousedown", handlePointer);
            document.addEventListener("keydown", handleKey);
        }, 0);

        return () => {
            window.clearTimeout(timer);
            if (handlePointer) document.removeEventListener("mousedown", handlePointer);
            if (handleKey) document.removeEventListener("keydown", handleKey);
        };
    }, [markAllNotificationsRead, setShowNotificationPanel, showNotificationPanel]);

    return (
        <div ref={buttonRef} className="relative">
            <FunctionalButton
                className={`relative ${className} ${
                    hasUnreadNotifications()
                        ? "after:absolute after:right-0 after:top-0 after:h-1.5 after:w-1.5 after:rounded-full after:bg-primary"
                        : ""
                }`}
                icon={<NotificationIcon className={iconClassName} />}
                onClick={() => setShowNotificationPanel(!showNotificationPanel)}
            />
            <Portal visible={showNotificationPanel} target={document.body}>
                <div
                    ref={panelRef}
                    className="fixed z-[10050]"
                    style={{ top: panelPos.top, right: panelPos.right }}
                >
                    <NotificationPanel />
                </div>
            </Portal>
        </div>
    );
};

export default NotificationButton;
